import moment from "moment";
import ToggleButtonGroupComponent from "../../components/common/toggle-button-group.component";
import FileUploadInputComponent from "../../components/file/file-upload-input.component";
import DynamicDateComponent from "../../components/report/dynamic-date.component";
import DynamicReportFilterComponent from "../../components/report/dynamic-report-filter.component";
import SingleSelectComponent from "../../components/single-select/single-select.component";
import { DATE_TYPE_CODES, FILE_EXTENSIONS, formats } from "../../config/constants";
import RawDataModel from "../../models/raw-data/raw-data.model";
import ViewBase from "../view-base.view";
import languageModel from "../../models/language.model";

/**
 * Raw Data Upload View
 * 
 * Main view page for uploading Raw Data, searching Raw Data uploads and downloading uploaded Raw Data. 
 * 
 * @author Darrus Goh 
 */
export default function RawDataUploadView() {
    ViewBase.call(this);

    const FILENAME_UPLOAD_RESULT = (originalFileName, type) => `${originalFileName.substring(0, originalFileName.lastIndexOf('.'))}_${type.toUpperCase()}${originalFileName.substring(originalFileName.lastIndexOf('.'), originalFileName.length)}`;
    const FILENAME_UPLOAD_TEMPLATE = () => `${selectedDataType?.name.replace('.', '_')}_Template`;
    const FILENAME_UPLOAD_DATA = () => `${selectedDataType?.name.replace('.', '_')}_Data_${API_DOWNLOAD_REQUEST_PARAMS.fromDate}_${API_DOWNLOAD_REQUEST_PARAMS.toDate}`;

    const CONFIG_TYPE_DATE = 'DateType';
    const ID_VIEW_UPLOAD_BUTTON = 'RawDataViewUploadButton';
    const ID_VIEW_DATA_BUTTON = 'RawDataViewDataButton';
    const ID_DATA_TYPE_INPUT = 'RawDataDataTypeInput';
    const ID_FILE_UPLOAD = 'RawDataFileUpload';

    const BUTTON_VIEW_UPLOAD = {
        id: ID_VIEW_UPLOAD_BUTTON,
        get label() { return languageModel.getText('LT00001005', 'View Uploads') },
        get checked() { return selectedTab === ID_VIEW_UPLOAD_BUTTON }
    };

    const BUTTON_VIEW_DATA = {
        id: ID_VIEW_DATA_BUTTON,
        get label() { return languageModel.getText('LT00001006', 'Search Data') },
        get checked() { return selectedTab === ID_VIEW_DATA_BUTTON }
    };

    let sectionButtons = [BUTTON_VIEW_UPLOAD];

    const RESULT_TYPES = {
        TOTAL: 'total',
        SUCCESS: 'success',
        FAIL: 'fail',
        NO_CHANGE: 'nochange'
    };

    const ACTIONS = {
        UPLOAD: 0,
        SEARCH: 1,
        DOWNLOAD: 2
    };
    let submitAction = ACTIONS.UPLOAD;

    const DEFAULT_BASE_FILTERS = {
        periodCode: DATE_TYPE_CODES.DATE,
        fromDate: moment().format(formats.dateFormat),
        toDate: moment().format(formats.dateFormat),
        parameters: []
    };
    let baseFilters = structuredClone(DEFAULT_BASE_FILTERS);

    const API_DOWNLOAD_REQUEST_PARAMS = {
        get periodCode() { return baseFilters.periodCode },
        get fromDate() { return toApiDateFormat(this.periodCode, baseFilters.fromDate) },
        get toDate() { return toApiDateFormat(this.periodCode, baseFilters.toDate) },
        get parameters() {
            return baseFilters.parameters.map(param => {
                let value = '';

                if (typeof param.value === 'boolean') {
                    value = param.value.toString();
                } else if (Array.isArray(param.value)) {
                    if (typeof param.value[0] === 'object') {
                        value = param.value.map(element => element.code).join('^');
                    } else {
                        value = param.value.join('^');
                    }
                } else if (typeof param.value === 'object') {
                    value = param.value.code;
                } else {
                    value = param.value;
                }


                return `${param.key}:=${value}`;
            }).join('^^')
        }
    };

    const API_SEARCH_REQUEST_PARAMS = {
        get fromDate() { return toApiDateFormat(DATE_TYPE_CODES.DAY, baseFilters.fromDate) },
        get toDate() { return toApiDateFormat(DATE_TYPE_CODES.DAY, baseFilters.toDate) },
    };



    let dataTypes = [];
    let uploadedFilesInfo = [];

    let selectedTab = ID_VIEW_UPLOAD_BUTTON;
    let selectedDataType = null;
    let supportedDates = [];
    let fileToUpload = null;

    const getDataTypes = () => {
        dataTypes = [];
        return RawDataModel.getDataTypes('upload')
            .then(result => {
                dataTypes = result ?? [];
            })
            .catch(error => {
                this.handleError(error)
            });
    }

    const getConfigs = () => {
        supportedDates = [];
        return RawDataModel.getConfigs(selectedDataType?.id)
            .then(result => {
                if (result) {
                    for (const config of result) {
                        switch (config.configType) {
                            case CONFIG_TYPE_DATE: {
                                supportedDates = config.value1.split('^');
                                baseFilters.periodCode = supportedDates[0];
                            }
                        }
                    }
                }
                return supportedDates;
            })
            .catch(error => {
                this.handleError(error);
            });
    }

    const getFilters = () => {
        return RawDataModel.getFilters(selectedDataType?.id, selectedDataType?.location)
            .then(result => {
                if (result) {
                    return result.filter(filter => filter.config !== 'hide');
                }
                return [];
            })
            .catch(error => {
                this.handleError(error);
            });
    }

    const downloadUploadTemplate = () => {
        return RawDataModel.downloadUploadTemplate(selectedDataType?.id, FILENAME_UPLOAD_TEMPLATE())
            .catch(error => {
                this.handleError(error);
            });
    }

    const downloadRawDataUploadResultType = (headerId, type, originalFileName, location) => {
        return RawDataModel.downloadRawDataUploadResultType(headerId, type, FILENAME_UPLOAD_RESULT(originalFileName, type), location)
            .catch(error => {
                this.handleError(error);
            });
    }

    const handleSelectDataType = (selectedItem) => {
        this.showLoading();

        selectedDataType = selectedItem;
        baseFilters = structuredClone(DEFAULT_BASE_FILTERS);

        let buttons = [BUTTON_VIEW_UPLOAD];

        Promise.all([getConfigs(), getFilters()])
            .then(([supportedDates, rawDataFilters]) => {
                if (supportedDates.length > 0 || rawDataFilters.length > 0) {
                    buttons.push(BUTTON_VIEW_DATA);
                }
            })
            .finally(() => {
                sectionButtons = buttons;
                if (!sectionButtons.map(button => button.id).includes(selectedTab)) {
                    selectedTab = sectionButtons[0].id;
                }
                this.hideLoading();
            });
    }

    const onSubmit = (e) => {
        e.preventDefault();

        // Form validation
        const form = document.getElementsByTagName('form')[0];
        form.classList.add('was-validated');

        const fileInput = document.getElementById(ID_FILE_UPLOAD);

        if (!document.getElementById(ID_DATA_TYPE_INPUT).checkValidity()) {
            e.stopPropagation();
            return false;
        }

        switch (submitAction) {
            case ACTIONS.UPLOAD: {
                fileInput.setAttribute('required', true);
                if (!fileInput.checkValidity()) {
                    e.stopPropagation();
                    return false;
                }

                onUpload();
                break;
            }
            case ACTIONS.SEARCH:
                fileInput.removeAttribute('required');
                onSearch();
                break;
            case ACTIONS.DOWNLOAD:
                fileInput.removeAttribute('required');
                onDownload();
                break;
        }
    }

    const onUpload = () => {
        const formData = new FormData();
        formData.set('uploadedExcel', fileToUpload);

        this.showLoading();
        return RawDataModel.uploadRawDataUpload(selectedDataType?.id, formData, selectedDataType?.location)
            .then((result) => {
                if (result !== null && result.length > 0) {
                    this.showMessage(this.getText(result[0].Code, result[0].Message) ?? this.getText('LT00000914', 'The file has been uploaded successfully'), result[0].transStatus);
                } else {
                    this.showErrorMessage(this.getText('EM00000082', 'Invalid file. Cannot process.'), this.getText('EM00000058', 'Invalid file.'));
                }
            })
            .catch(error => {
                this.handleError(error);
            })
            .finally(() => { this.hideLoading(); });
    }

    const onSearch = () => {
        this.showLoading();
        return RawDataModel.getRawDataUploads(selectedDataType?.id, API_SEARCH_REQUEST_PARAMS.fromDate, API_SEARCH_REQUEST_PARAMS.toDate, selectedDataType?.location)
            .then((result) => {
                uploadedFilesInfo = result ?? [];
            })
            .catch((error) => {
                this.handleError(error);
            })
            .finally(() => {
                this.hideLoading();
            });
    }

    const onDownload = () => {
        this.showLoading();
        return RawDataModel.downloadRawDataUploadData(selectedDataType?.id, API_DOWNLOAD_REQUEST_PARAMS, selectedDataType?.location, FILENAME_UPLOAD_DATA())
            .catch((error) => {
                this.handleError(error);
            })
            .finally(() => { this.hideLoading(); });
    }

    const FilterWrapperComponent = () => {
        return {
            view: (vnode) => {
                return m(`.col-12`, vnode.attrs, vnode.children)
            }
        }
    }

    const toApiDateFormat = (dateType, date) => {
        switch (dateType) {
            case DATE_TYPE_CODES.DATE:
                return moment(date).format(formats.apiDateFormat);
            case DATE_TYPE_CODES.WEEK:
                return moment(date, formats.WEEK_FORMAT).format(formats.API_WEEK_FORMAT);
            case DATE_TYPE_CODES.MONTH:
                return moment(date).format(formats.API_MONTH_FORMAT);
        }
    }

    return {
        oninit: () => {
            getDataTypes();
        },
        view: () => [
            m('form.vstack.gap-2[novalidate]', { onsubmit: onSubmit },
                m('.card',
                    m('.card-body.row.gap-1',
                        m('.col-xl-5.position-relative',
                            m('label.form-label.mandatory', this.getText('LT00000013', 'Data Type')),
                            m(SingleSelectComponent, {
                                id: ID_DATA_TYPE_INPUT,
                                required: true,
                                allowEmpty: false,
                                itemKey: 'id',
                                selectedItem: selectedDataType,
                                items: dataTypes,
                                onSelect: handleSelectDataType
                            }),
                            m('.invalid-tooltip', this.getText("LT00000729", "This field is required"))
                        ),
                        m('.w-100'),
                        m('.col-xl-5.position-relative',
                            m('label.form-label.mandatory', this.getText('LT00000792', 'File Upload')),
                            m(FileUploadInputComponent, {
                                id: ID_FILE_UPLOAD,
                                required: submitAction === ACTIONS.UPLOAD, // Note that the real input is dynamically changed when onSubmit based on type of action submitted
                                disabled: !selectedDataType,
                                accept: `${FILE_EXTENSIONS.XLSX}, ${FILE_EXTENSIONS.XLS}`,
                                onTemplateDownloadClick: downloadUploadTemplate,
                                onInput: (uploadedFile) => {
                                    fileToUpload = uploadedFile;
                                }
                            }),
                            m('.invalid-tooltip', this.getText("LT00000729", "This field is required"))
                        ),
                        m('.col-12.hstack.justify-content-end',
                            m('button.btn.btn-primary', { onclick: () => { submitAction = ACTIONS.UPLOAD } }, this.getText('LT00000290', 'Upload'))
                        )
                    )
                ),
                m('.card',
                    m('.card-body.vstack.gap-2',
                        m('.row.g-2',
                            m('.col-xl-4',
                                m(ToggleButtonGroupComponent, {
                                    buttonType: 'radio',
                                    onChange: (e) => {
                                        selectedTab = e.target.id;
                                    },
                                    buttons: sectionButtons
                                })
                            ),
                            m('.w-100'),
                            m('.col-xl-3',
                                m(DynamicDateComponent, {
                                    selectedDateType: selectedTab === ID_VIEW_UPLOAD_BUTTON ? DATE_TYPE_CODES.DATE : baseFilters.periodCode,
                                    supportedDates: selectedTab === ID_VIEW_UPLOAD_BUTTON ? [DATE_TYPE_CODES.DATE] : supportedDates,
                                    required: true,
                                    fromDate: baseFilters.fromDate,
                                    toDate: baseFilters.toDate,
                                    onChange: (dateType, fromDate, toDate) => {
                                        // baseFilters.periodCode = dateType;
                                        baseFilters.fromDate = fromDate;
                                        baseFilters.toDate = toDate;
                                    }
                                })
                            ),
                            m('.w-100'), // Empty col to push dynamic report filter component down
                            m('.col-lg-4',
                                m('.row.g-2',
                                    selectedTab === ID_VIEW_DATA_BUTTON && m(DynamicReportFilterComponent, {
                                        filterWrapperComponent: FilterWrapperComponent,
                                        buttonWrapperComponent: FilterWrapperComponent,
                                        isRawData: true,
                                        data: {
                                            dataTypeId: selectedDataType?.id,
                                            parameters: baseFilters.parameters
                                        },
                                        onChange: (data) => {
                                            baseFilters.parameters = data;
                                        }
                                    })
                                )
                            )
                        ),
                        m('.hstack.justify-content-end',
                            selectedTab === ID_VIEW_UPLOAD_BUTTON
                                ? m('button.btn.btn-primary', { onclick: () => { submitAction = ACTIONS.SEARCH } }, this.getText('LT00000001', 'Search'))
                                : m('button.btn.btn-primary', { onclick: () => { submitAction = ACTIONS.DOWNLOAD } }, this.getText('LT00000492', 'Download'))
                        )
                    )
                ),
                selectedTab === ID_VIEW_UPLOAD_BUTTON && m('.card',
                    m('.card-body',
                        m('.table-responsive',
                            m('table.table',
                                m('thead',
                                    m('tr',
                                        m('th', 'ID'),
                                        m('th', 'Upload By'),
                                        m('th', 'File Name'),
                                        m('th', 'Upload Date'),
                                        m('th', 'Status'),
                                        m('th', 'Total Rows'),
                                        m('th', 'Success'),
                                        m('th', 'Fail'),
                                        m('th', 'No Change')
                                    )
                                ),
                                m('tbody',
                                uploadedFilesInfo && uploadedFilesInfo.length > 0
                                        ? uploadedFilesInfo.map((file) => m('tr',
                                            m('td', file.headerId),
                                            m('td', file.uploadUserId),
                                            m('td', file.uploadFileName),
                                            m('td', file.uploadDate),
                                            m('td', file.status),
                                            m('td',
                                                file.totalRows
                                                    ? m('a.link-primary', {
                                                        href: '#',
                                                        onclick: (e) => {
                                                            e.preventDefault();
                                                            downloadRawDataUploadResultType(file.headerId, RESULT_TYPES.TOTAL, file.uploadFileName, selectedDataType?.location || '')
                                                        }
                                                    }, file.totalRows)
                                                    : 0
                                            ),
                                            m('td',
                                                file.successRows
                                                    ? m('a.link-primary', {
                                                        href: '#',
                                                        onclick: (e) => {
                                                            e.preventDefault();
                                                            downloadRawDataUploadResultType(file.headerId, RESULT_TYPES.SUCCESS, file.uploadFileName, selectedDataType?.location || '')
                                                        }
                                                    }, file.successRows)
                                                    : 0
                                            ),
                                            m('td',
                                                file.errorRows
                                                    ? m('a.link-primary', {
                                                        href: '#',
                                                        onclick: (e) => {
                                                            e.preventDefault();
                                                            downloadRawDataUploadResultType(file.headerId, RESULT_TYPES.FAIL, file.uploadFileName, selectedDataType?.location || '')
                                                        }
                                                    }, file.errorRows)
                                                    : 0
                                            ),
                                            m('td',
                                                file.noChangeRows
                                                    ? m('a.link-primary', {
                                                        href: '#',
                                                        onclick: (e) => {
                                                            e.preventDefault();
                                                            downloadRawDataUploadResultType(file.headerId, RESULT_TYPES.NO_CHANGE, file.uploadFileName, selectedDataType?.location || '')
                                                        }
                                                    }, file.noChangeRows)
                                                    : 0
                                            )
                                        ))
                                        : m('td', { colspan: '100%' }, this.getText("EM00000141", "No records found."))
                                )
                            )
                        )
                    )
                )
            ),
            this.getBaseViews()
        ]
    }
}